import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    AccountGroup,
    CashFlowConfig,
    CashFlowConfigSaveRequest,
    GUID,
    PlannedWithdrawalSaveRequest,
} from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';
import { formatDate } from 'utils';

export const useCashFlowConfigs = (accountGroupGUID: GUID<AccountGroup>) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'cashFlowConfig'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listCashFlowConfigs(accountGroupGUID);
            return response ?? undefined;
        },
    });
};

export const useWithdrawals = (startDate: Date, endDate: Date) => {
    return useQuery({
        queryKey: ['withdrawals', startDate, endDate],
        placeholderData: keepPreviousData, // data is still kept around even when dates change until refetched!
        queryFn: async () => {
            const [response] = await PortfolioApi().getPlannedWithdrawals({
                startDate: formatDate(startDate, 'SERVER'),
                endDate: formatDate(endDate, 'SERVER'),
            });
            return response ?? [];
        },
    });
};

export const useWithdrawalEvents = (cashFlowConfigGUID: GUID<CashFlowConfig>, withdrawalDate: DateStr) => {
    return useQuery({
        queryKey: ['withdrawal-events', cashFlowConfigGUID, withdrawalDate],
        queryFn: async () => {
            const [response] = await PortfolioApi().getPlannedWithdrawalEvents(cashFlowConfigGUID, { withdrawalDate });
            return response ?? undefined;
        },
    });
};

export const useSavePlannedWithdrawal = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: PlannedWithdrawalSaveRequest) => PortfolioApi().savePlannedWithdrawal(request),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
        },
    });
};

export const useCreateCashFlowConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: { accountGroupGUID: GUID<AccountGroup>; cashFlowConfig: CashFlowConfigSaveRequest }) =>
            PortfolioApi().createCashFlowConfig(request.accountGroupGUID, request.cashFlowConfig),
        onSuccess: (_, request) => {
            void queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
            void queryClient.invalidateQueries({
                queryKey: ['accountGroup', request.accountGroupGUID, 'cashFlowList'],
            });
            void queryClient.invalidateQueries({
                queryKey: ['accountGroup', request.accountGroupGUID, 'monthlyCashFlow'],
            });
        },
    });
};
